import { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router';
import { Table as TableIcon, Package, LayoutGrid01 } from '@untitledui/icons';
import {
    SearchLg,
    Eye
} from '@untitledui/icons';
import { Input } from '@/components/base/input/input';
import { Select } from '@/components/base/select/select';
import { Badge } from '@/components/base/badges/badges';
import { Table, TableCard } from '@/components/application/table/table';
import { ProgressBar } from '@/components/base/progress-indicators/progress-indicators';
import { DistributionChart } from '@/components/application/charts/distribution-chart';
import { containerService } from '@/data/mock-containers';
import type { Container, ContainerFilters, ContainerStatus, RiskLevel, DocumentStatus } from '@/types/container';
import { useTranslation } from 'react-i18next';
import { useI18n } from '@/hooks/use-i18n';
import { useMemo } from 'react';
import WorldRoutesMapPreview from '@/components/application/map/world-routes-map-preview';
import { ContainerListView } from '@/components/application/containers/container-list-view';
import { Button } from '@/components/base/buttons/button';
import { useInfiniteScroll } from '@/components/application/containers/use-infinite-scroll';

type ViewMode = 'recap' | 'table' | 'list';

export const FreightForwarderContainersPage = () => {
    const { t } = useTranslation(['pages:containers', 'common', 'navigation']);
    const { translateContainerStatus, translateDocumentStatus, translateRiskLevel } = useI18n();
    const [containers, setContainers] = useState<Container[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState<ContainerStatus | ''>('');
    const [riskLevelFilter, setRiskLevelFilter] = useState<RiskLevel | ''>('');
    const [documentStatusFilter, setDocumentStatusFilter] = useState<DocumentStatus | ''>('');
    const [shippingCompanyFilter, setShippingCompanyFilter] = useState<string | ''>('');
    const [shippingCompanyOptions, setShippingCompanyOptions] = useState<{ id: string; label: string }[]>([]);
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const getValidViewMode = (view: string | null): ViewMode => {
        const validModes: ViewMode[] = ['recap', 'table', 'list'];
        return (validModes.includes(view as ViewMode) ? view : 'recap') as ViewMode;
    };
    const [viewMode, setViewMode] = useState<ViewMode>(
        getValidViewMode(searchParams.get('view'))
    );
    const [displayedCount, setDisplayedCount] = useState(20);

    // Aggiorna query parameter quando viewMode cambia
    useEffect(() => {
        const currentView = searchParams.get('view');
        if (viewMode !== currentView) {
            const newParams = new URLSearchParams(searchParams);
            if (viewMode === 'recap') {
                newParams.delete('view');
            } else {
                newParams.set('view', viewMode);
            }
            setSearchParams(newParams, { replace: true });
        }
    }, [viewMode, searchParams, setSearchParams]);

    // Reset displayedCount quando cambia viewMode o filtri
    useEffect(() => {
        setDisplayedCount(20);
    }, [viewMode, searchTerm, statusFilter, riskLevelFilter, documentStatusFilter, shippingCompanyFilter]);

    // Container da mostrare (con lazy loading per list/table)
    const displayedContainers = useMemo(() => {
        if (viewMode === 'recap') {
            // In recap mostra solo gli ultimi 10
            return containers.slice(0, 10);
        }
        // Per altre viste, mostra fino a displayedCount
        return containers.slice(0, displayedCount);
    }, [containers, displayedCount, viewMode]);

    const hasMore = displayedCount < containers.length;

    const { loadMoreRef } = useInfiniteScroll({
        hasMore,
        loadMore: () => {
            setDisplayedCount(prev => Math.min(prev + 20, containers.length));
        },
    });

    const statusOptions = [
        { id: '', label: t('list.filters.allStatuses') },
        { id: 'In Transit', label: t('containerStatus.inTransit', { ns: 'types' }) },
        { id: 'Delivered', label: t('containerStatus.delivered', { ns: 'types' }) },
        { id: 'Delayed', label: t('containerStatus.delayed', { ns: 'types' }) },
        { id: 'At Port', label: t('containerStatus.atPort', { ns: 'types' }) }
    ];

    const riskLevelOptions = [
        { id: '', label: t('list.filters.allLevels') },
        { id: 'Low', label: t('riskLevel.low', { ns: 'types' }) },
        { id: 'Medium', label: t('riskLevel.medium', { ns: 'types' }) },
        { id: 'High', label: t('riskLevel.high', { ns: 'types' }) },
        { id: 'Critical', label: t('riskLevel.critical', { ns: 'types' }) }
    ];

    const documentStatusOptions = [
        { id: '', label: t('list.filters.allStatuses') },
        { id: 'Completo', label: t('documentStatus.complete', { ns: 'types' }) },
        { id: 'Pending', label: t('documentStatus.pending', { ns: 'types' }) },
        { id: 'Error', label: t('documentStatus.error', { ns: 'types' }) }
    ];

    useEffect(() => {
        const loadContainers = async () => {
            setIsLoading(true);
            await new Promise(resolve => setTimeout(resolve, 500));

            // Filtra container con bookingNumber (presumendo che siano quelli per cui il FF ha fornito il booking)
            const allContainers = containerService.getAll();
            const containersWithBooking = allContainers.filter(
                c => c.bookingNumber !== undefined && c.bookingNumber !== null && c.bookingNumber !== ''
            );

            const filters: ContainerFilters = {
                search: searchTerm || undefined,
                status: statusFilter || undefined,
                riskLevel: riskLevelFilter || undefined,
                documentStatus: documentStatusFilter || undefined,
                shippingCompany: shippingCompanyFilter || undefined
            };

            // Applica filtri aggiuntivi
            let filtered = containersWithBooking;
            if (filters.search) {
                filtered = filtered.filter(c =>
                    c.containerNumber?.toLowerCase().includes(filters.search!.toLowerCase()) ||
                    c.route.from.toLowerCase().includes(filters.search!.toLowerCase()) ||
                    c.route.to.toLowerCase().includes(filters.search!.toLowerCase())
                );
            }
            if (filters.status) {
                filtered = filtered.filter(c => c.status === filters.status);
            }
            if (filters.riskLevel) {
                filtered = filtered.filter(c => c.riskLevel === filters.riskLevel);
            }
            if (filters.documentStatus) {
                filtered = filtered.filter(c => c.documentStatus === filters.documentStatus);
            }
            if (filters.shippingCompany) {
                filtered = filtered.filter(c => c.shippingCompany === filters.shippingCompany);
            }

            setContainers(filtered);
            setIsLoading(false);
        };

        // build shipping company options from all containers
        const all = containerService.getAll();
        const setOpts = Array.from(new Set(all.map(c => c.shippingCompany).filter(Boolean) as string[]))
            .sort()
            .map(v => ({ id: v, label: v }));
        setShippingCompanyOptions([{ id: '', label: t('list.filters.allCompanies') }, ...setOpts]);

        loadContainers();
    }, [searchTerm, statusFilter, riskLevelFilter, documentStatusFilter, shippingCompanyFilter, t]);

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'In Transit': return 'brand';
            case 'Delivered': return 'success';
            case 'Delayed': return 'error';
            case 'At Port': return 'warning';
            default: return 'gray';
        }
    };

    const getColorHex = (colorName: string): string => {
        switch (colorName) {
            case 'brand': return '#6938ef';
            case 'success': return '#079485';
            case 'warning': return '#dc6803';
            case 'error': return '#d92d20';
            default: return '#6b7280';
        }
    };

    const distributionData = useMemo(() => {
        const total = containers.length;

        const riskDistribution = containers.reduce((acc, c) => {
            acc[c.riskLevel] = (acc[c.riskLevel] || 0) + 1;
            return acc;
        }, {} as Record<RiskLevel, number>);

        const riskData = [
            { label: translateRiskLevel('Low'), value: riskDistribution.Low || 0, color: getColorHex('success') },
            { label: translateRiskLevel('Medium'), value: riskDistribution.Medium || 0, color: getColorHex('warning') },
            { label: translateRiskLevel('High'), value: riskDistribution.High || 0, color: getColorHex('error') },
            { label: translateRiskLevel('Critical'), value: riskDistribution.Critical || 0, color: getColorHex('error') },
        ];

        const statusDistribution = containers.reduce((acc, c) => {
            acc[c.status] = (acc[c.status] || 0) + 1;
            return acc;
        }, {} as Record<ContainerStatus, number>);

        const statusData = [
            { label: translateContainerStatus('In Transit'), value: statusDistribution['In Transit'] || 0, color: getColorHex('brand') },
            { label: translateContainerStatus('Delivered'), value: statusDistribution.Delivered || 0, color: getColorHex('success') },
            { label: translateContainerStatus('Delayed'), value: statusDistribution.Delayed || 0, color: getColorHex('error') },
            { label: translateContainerStatus('At Port'), value: statusDistribution['At Port'] || 0, color: getColorHex('warning') },
        ];

        const documentDistribution = containers.reduce((acc, c) => {
            acc[c.documentStatus] = (acc[c.documentStatus] || 0) + 1;
            return acc;
        }, {} as Record<DocumentStatus, number>);

        const documentData = [
            { label: translateDocumentStatus('Completo'), value: documentDistribution.Completo || 0, color: getColorHex('success') },
            { label: translateDocumentStatus('Pending'), value: documentDistribution.Pending || 0, color: getColorHex('warning') },
            { label: translateDocumentStatus('Error'), value: documentDistribution.Error || 0, color: getColorHex('error') },
        ];

        return { riskData, statusData, documentData, total };
    }, [containers, translateRiskLevel, translateContainerStatus, translateDocumentStatus]);

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <p className="text-lg text-tertiary">{t('list.loading')}</p>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-semibold text-primary">Container</h1>
                    <p className="text-tertiary mt-1">
                        Container con booking number ({containers.length})
                    </p>
                </div>
                <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2 rounded-lg bg-secondary p-1 ring-1 ring-secondary_alt">
                        <Button
                            size="sm"
                            color={viewMode === 'recap' ? 'primary' : 'secondary'}
                            onClick={() => setViewMode('recap')}
                            iconLeading={LayoutGrid01}
                        >
                            Recap
                        </Button>
                        <Button
                            size="sm"
                            color={viewMode === 'list' ? 'primary' : 'secondary'}
                            onClick={() => setViewMode('list')}
                            iconLeading={Package}
                        >
                            Lista
                        </Button>
                        <Button
                            size="sm"
                            color={viewMode === 'table' ? 'primary' : 'secondary'}
                            onClick={() => setViewMode('table')}
                            iconLeading={TableIcon}
                        >
                            Tabella
                        </Button>
                    </div>
                </div>
            </div>

            {/* World Routes Map Preview e Distribution Charts - solo in modalità Recap */}
            {viewMode === 'recap' && (
                <>
                    {/* World Routes Map Preview */}
                    <div className="rounded-xl bg-primary p-6 shadow-xs ring-1 ring-secondary">
                        <div className="flex items-center justify-between mb-4">
                            <div>
                                <h3 className="text-lg font-semibold text-primary">{t('worldMap.title')}</h3>
                                <p className="text-sm text-tertiary">{t('worldMap.updateInfo')}</p>
                            </div>
                        </div>
                        <WorldRoutesMapPreview height={360} role="freight-forwarder" />
                    </div>

                    {/* Distribution Charts */}
                    <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
                        <DistributionChart
                            title={t('list.charts.riskLevels', { ns: 'pages:containers' }) || 'Risk Levels Distribution'}
                            data={distributionData.riskData}
                            total={distributionData.total}
                        />
                        <DistributionChart
                            title={t('list.charts.containerStatus', { ns: 'pages:containers' }) || 'Container Status Distribution'}
                            data={distributionData.statusData}
                            total={distributionData.total}
                        />
                        <DistributionChart
                            title={t('list.charts.documentStatus', { ns: 'pages:containers' }) || 'Document Status Distribution'}
                            data={distributionData.documentData}
                            total={distributionData.total}
                        />
                    </div>
                </>
            )}

            {/* Filters */}
            <div className="rounded-xl bg-primary p-6 shadow-xs ring-1 ring-secondary">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
                    <Input
                        placeholder={t('list.filters.searchPlaceholder')}
                        value={searchTerm}
                        onChange={setSearchTerm}
                        icon={SearchLg}
                        size="md"
                    />

                    <Select
                        placeholder={t('list.filters.statusPlaceholder')}
                        items={statusOptions}
                        selectedKey={statusFilter}
                        onSelectionChange={(key) => setStatusFilter(key as ContainerStatus | '')}
                        size="md"
                    >
                        {(item) => (
                            <Select.Item key={item.id} id={item.id}>
                                {item.label}
                            </Select.Item>
                        )}
                    </Select>

                    <Select
                        placeholder={t('list.filters.riskLevelPlaceholder')}
                        items={riskLevelOptions}
                        selectedKey={riskLevelFilter}
                        onSelectionChange={(key) => setRiskLevelFilter(key as RiskLevel | '')}
                        size="md"
                    >
                        {(item) => (
                            <Select.Item key={item.id} id={item.id}>
                                {item.label}
                            </Select.Item>
                        )}
                    </Select>

                    <Select
                        placeholder={t('list.filters.documentStatusPlaceholder')}
                        items={documentStatusOptions}
                        selectedKey={documentStatusFilter}
                        onSelectionChange={(key) => setDocumentStatusFilter(key as DocumentStatus | '')}
                        size="md"
                    >
                        {(item) => (
                            <Select.Item key={item.id} id={item.id}>
                                {item.label}
                            </Select.Item>
                        )}
                    </Select>

                    <Select
                        placeholder={t('list.filters.shippingCompanyPlaceholder')}
                        items={shippingCompanyOptions}
                        selectedKey={shippingCompanyFilter}
                        onSelectionChange={(key) => setShippingCompanyFilter(key as string | '')}
                        size="md"
                    >
                        {(item) => (
                            <Select.Item key={item.id} id={item.id}>
                                {item.label}
                            </Select.Item>
                        )}
                    </Select>
                </div>
            </div>

            {/* Containers View */}
            {viewMode === 'recap' ? (
                <div className="space-y-6">
                    {/* Ultimi 10 container */}
                    <div>
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-semibold text-primary">
                                Ultimi 10 Container ({displayedContainers.length})
                            </h3>
                            <Link
                                to="/freight-forwarder/containers"
                                className="text-sm text-brand-secondary hover:text-brand-secondary_hover"
                                onClick={(e) => {
                                    e.preventDefault();
                                    setViewMode('list');
                                }}
                            >
                                Vedi tutti →
                            </Link>
                        </div>
                        <TableCard.Root>
                            <Table>
                                <Table.Header>
                                    <Table.Head label={t('list.table.headers.containerNumber')} isRowHeader />
                                    <Table.Head label="Booking Number" />
                                    <Table.Head label={t('list.table.headers.route')} />
                                    <Table.Head label="Shipping Company" />
                                    <Table.Head label={t('list.table.headers.progress')} />
                                    <Table.Head label="ETA" />
                                    <Table.Head label={t('list.table.headers.status')} />
                                    <Table.Head label={t('list.table.headers.actions')} />
                                </Table.Header>
                                <Table.Body>
                                    {displayedContainers.map((container) => (
                                        <Table.Row key={container.id}>
                                            <Table.Cell>
                                                <div className="font-medium text-primary">{container.containerNumber}</div>
                                            </Table.Cell>
                                            <Table.Cell>
                                                <div className="text-sm text-tertiary">{container.bookingNumber ?? '-'}</div>
                                            </Table.Cell>
                                            <Table.Cell>
                                                <div className="text-sm text-tertiary">
                                                    {container.route.from} → {container.route.to}
                                                </div>
                                            </Table.Cell>
                                            <Table.Cell>
                                                <div className="text-sm text-tertiary">{container.shippingCompany ?? '-'}</div>
                                            </Table.Cell>
                                            <Table.Cell>
                                                <div className="flex items-center gap-2">
                                                    <ProgressBar
                                                        value={container.progress}
                                                        min={0}
                                                        max={100}
                                                    />
                                                    <span className="text-sm text-tertiary">{container.progress}%</span>
                                                </div>
                                            </Table.Cell>
                                            <Table.Cell>
                                                <span className="text-sm text-tertiary">
                                                    {container.eta.toLocaleDateString('it-IT')}
                                                </span>
                                            </Table.Cell>
                                            <Table.Cell>
                                                <Badge color={getStatusColor(container.status)} size="sm">
                                                    {translateContainerStatus(container.status)}
                                                </Badge>
                                            </Table.Cell>
                                            <Table.Cell>
                                                <div className="flex items-center gap-2">
                                                    <Link
                                                        to={`/freight-forwarder/containers/${container.id}`}
                                                        className="text-brand-secondary hover:text-brand-secondary_hover"
                                                    >
                                                        <Eye className="size-4" />
                                                    </Link>
                                                </div>
                                            </Table.Cell>
                                        </Table.Row>
                                    ))}
                                </Table.Body>
                            </Table>
                        </TableCard.Root>
                    </div>
                </div>
            ) : viewMode === 'list' ? (
                <div className="space-y-4">
                    <div className="flex items-center justify-between">
                        <h3 className="text-lg font-semibold text-primary">
                            Container ({containers.length})
                        </h3>
                    </div>
                    <ContainerListView
                        containers={displayedContainers}
                        onContainerSelect={(id) => {
                            // Navigate to container detail preserving view
                            const currentView = searchParams.get('view');
                            const url = currentView 
                                ? `/freight-forwarder/containers/${id}?view=${currentView}`
                                : `/freight-forwarder/containers/${id}`;
                            navigate(url);
                        }}
                    />
                    {hasMore && (
                        <div ref={loadMoreRef} className="flex justify-center py-4">
                            <div className="text-sm text-tertiary">Caricamento...</div>
                        </div>
                    )}
                </div>
            ) : (
                <>
                    <TableCard.Root>
                        <TableCard.Header
                            title={`Container (${containers.length})`}
                            description="Container con booking number"
                        />

                        <Table>
                            <Table.Header>
                                <Table.Head label={t('list.table.headers.containerNumber')} isRowHeader />
                                <Table.Head label="Booking Number" />
                                <Table.Head label={t('list.table.headers.route')} />
                                <Table.Head label="Shipping Company" />
                                <Table.Head label={t('list.table.headers.progress')} />
                                <Table.Head label="ETA" />
                                <Table.Head label={t('list.table.headers.status')} />
                                <Table.Head label={t('list.table.headers.actions')} />
                            </Table.Header>
                            <Table.Body>
                                {displayedContainers.map((container) => (
                                    <Table.Row key={container.id}>
                                        <Table.Cell>
                                            <div className="font-medium text-primary">{container.containerNumber}</div>
                                        </Table.Cell>
                                        <Table.Cell>
                                            <div className="text-sm text-tertiary">{container.bookingNumber ?? '-'}</div>
                                        </Table.Cell>
                                        <Table.Cell>
                                            <div className="text-sm text-tertiary">
                                                {container.route.from} → {container.route.to}
                                            </div>
                                        </Table.Cell>
                                        <Table.Cell>
                                            <div className="text-sm text-tertiary">{container.shippingCompany ?? '-'}</div>
                                        </Table.Cell>
                                        <Table.Cell>
                                            <div className="flex items-center gap-2">
                                                <ProgressBar
                                                    value={container.progress}
                                                    min={0}
                                                    max={100}
                                                />
                                                <span className="text-sm text-tertiary">{container.progress}%</span>
                                            </div>
                                        </Table.Cell>
                                        <Table.Cell>
                                            <span className="text-sm text-tertiary">
                                                {container.eta.toLocaleDateString('it-IT')}
                                            </span>
                                        </Table.Cell>
                                        <Table.Cell>
                                            <Badge color={getStatusColor(container.status)} size="sm">
                                                {translateContainerStatus(container.status)}
                                            </Badge>
                                        </Table.Cell>
                                        <Table.Cell>
                                            <div className="flex items-center gap-2">
                                                <Link
                                                    to={(() => {
                                                        const currentView = searchParams.get('view');
                                                        return currentView 
                                                            ? `/freight-forwarder/containers/${container.id}?view=${currentView}`
                                                            : `/freight-forwarder/containers/${container.id}`;
                                                    })()}
                                                    className="text-brand-secondary hover:text-brand-secondary_hover"
                                                >
                                                    <Eye className="size-4" />
                                                </Link>
                                            </div>
                                        </Table.Cell>
                                    </Table.Row>
                                ))}
                            </Table.Body>
                        </Table>
                    </TableCard.Root>
                    {hasMore && (
                        <div ref={loadMoreRef} className="flex justify-center py-4">
                            <div className="text-sm text-tertiary">Caricamento...</div>
                        </div>
                    )}
                </>
            )}

            {containers.length === 0 && (
                <div className="text-center py-12">
                    <div className="text-tertiary mb-4">
                        <Package className="size-12 mx-auto mb-4" />
                        <h3 className="text-lg font-medium text-primary mb-2">Nessun container trovato</h3>
                        <p className="text-tertiary">
                            Non ci sono container con booking number disponibili.
                        </p>
                    </div>
                </div>
            )}
        </div>
    );
};

